import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { 
  Settings, User, Clock, Save, Loader2,
  CreditCard, AlertTriangle, CheckCircle2, Banknote,
  Mail, Phone, Key, Building2, Shield
} from 'lucide-react';

const DAYS_OF_WEEK = [
  { id: 1, name: 'Segunda', short: 'Seg' },
  { id: 2, name: 'Terça', short: 'Ter' },
  { id: 3, name: 'Quarta', short: 'Qua' },
  { id: 4, name: 'Quinta', short: 'Qui' },
  { id: 5, name: 'Sexta', short: 'Sex' },
  { id: 6, name: 'Sábado', short: 'Sáb' },
  { id: 0, name: 'Domingo', short: 'Dom' },
];

const PIX_TYPES = [
  { value: 'cpf', label: 'CPF', icon: User, placeholder: '000.000.000-00' },
  { value: 'cnpj', label: 'CNPJ', icon: Building2, placeholder: '00.000.000/0001-00' },
  { value: 'email', label: 'E-mail', icon: Mail, placeholder: 'seu@email.com' },
  { value: 'phone', label: 'Telefone', icon: Phone, placeholder: '+55 11 99999-9999' },
  { value: 'random', label: 'Chave Aleatória', icon: Key, placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
];

export default function ProviderSettings() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Dados do perfil (somente leitura)
  const [profile, setProfile] = useState({
    business_name: '',
    owner_name: '',
    email: '',
    document: ''
  });
  
  // Disponibilidade
  const [availability, setAvailability] = useState([]);
  
  // Dados de Pagamento (Pix)
  const [payoutData, setPayoutData] = useState({
    pix_type: '',
    pix_key: '',
    holder_name: '',
    holder_document: ''
  });
  const [hasPayoutConfigured, setHasPayoutConfigured] = useState(false);
  const [existingPayout, setExistingPayout] = useState(null);
  const [savingPayout, setSavingPayout] = useState(false);
  const [loadingPayout, setLoadingPayout] = useState(true);
  const [hasInProgressService, setHasInProgressService] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // Verificar se há token antes de redirecionar
    const token = localStorage.getItem('token');
    
    // Se não há token, redirecionar imediatamente
    if (!token) {
      navigate('/provider/login');
      return;
    }
    
    // Se há token mas user ainda não carregou, aguardar
    if (!user) {
      return;
    }
    
    // User carregou, verificar se é provider
    setAuthChecked(true);
    if (user.user_type !== 'provider') {
      navigate('/provider/login');
      return;
    }
    
    fetchProfileAndAvailability();
    fetchPayoutData();
  }, [user, navigate]);

  const fetchProfileAndAvailability = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Buscar dados do dashboard (inclui business_name)
      const dashboardRes = await axios.get(`${API}/provider/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Buscar disponibilidade existente
      const availRes = await axios.get(`${API}/provider/availability`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Preencher perfil com dados disponíveis
      setProfile({
        business_name: dashboardRes.data.business_name || user?.name || '',
        owner_name: user?.name || '',
        email: user?.email || '',
        document: '' // Documento não exposto na API por segurança
      });
      
      setAvailability(availRes.data.availability || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      // Mesmo com erro, tentar preencher com dados do user context
      setProfile({
        business_name: user?.name || '',
        owner_name: user?.name || '',
        email: user?.email || '',
        document: ''
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPayoutData = async () => {
    setLoadingPayout(true);
    try {
      const token = localStorage.getItem('token');
      
      // Buscar dados de pagamento
      const payoutRes = await axios.get(`${API}/provider/payouts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (payoutRes.data.has_payout && payoutRes.data.payout) {
        setHasPayoutConfigured(true);
        setExistingPayout(payoutRes.data.payout);
        setPayoutData({
          pix_type: payoutRes.data.payout.pix_type || '',
          pix_key: payoutRes.data.payout.pix_key || '',
          holder_name: payoutRes.data.payout.holder_name || '',
          holder_document: '' // Não mostrar documento completo
        });
      } else {
        setHasPayoutConfigured(false);
        // Preencher com dados do usuário se disponíveis
        setPayoutData({
          pix_type: '',
          pix_key: '',
          holder_name: user?.name || '',
          holder_document: ''
        });
      }
      
      // Verificar se tem serviço em andamento
      const appointmentsRes = await axios.get(`${API}/provider/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const appointments = appointmentsRes.data.appointments || [];
      const inProgress = appointments.some(a => a.status === 'IN_PROGRESS');
      setHasInProgressService(inProgress);
      
    } catch (error) {
      console.error('Erro ao carregar dados de pagamento:', error);
    } finally {
      setLoadingPayout(false);
    }
  };

  const handleSaveAvailability = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/provider/availability`, { availability }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Horários atualizados com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar horários');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePayout = async () => {
    // Validações
    if (!payoutData.pix_type) {
      toast.error('Selecione o tipo de chave Pix');
      return;
    }
    
    if (!payoutData.pix_key) {
      toast.error('Informe a chave Pix');
      return;
    }
    
    if (!payoutData.holder_name || payoutData.holder_name.length < 3) {
      toast.error('Informe o nome do titular (mínimo 3 caracteres)');
      return;
    }
    
    if (!payoutData.holder_document) {
      toast.error('Informe o CPF/CNPJ do titular');
      return;
    }
    
    setSavingPayout(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/provider/payouts`, {
        pix_type: payoutData.pix_type,
        pix_key: payoutData.pix_key,
        holder_name: payoutData.holder_name,
        holder_document: payoutData.holder_document
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(res.data.message || 'Dados de pagamento salvos com sucesso!');
      setHasPayoutConfigured(true);
      
      // Recarregar dados
      fetchPayoutData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar dados de pagamento');
    } finally {
      setSavingPayout(false);
    }
  };

  const toggleDayAvailability = (dayId) => {
    const existingDay = availability.find(a => a.day === dayId);
    
    if (existingDay) {
      setAvailability(prev => prev.filter(a => a.day !== dayId));
    } else {
      setAvailability(prev => [...prev, {
        day: dayId,
        day_name: DAYS_OF_WEEK.find(d => d.id === dayId)?.name || '',
        start: '08:00',
        end: '18:00'
      }]);
    }
  };

  const updateDayTime = (dayId, field, value) => {
    setAvailability(prev => prev.map(a => 
      a.day === dayId ? { ...a, [field]: value } : a
    ));
  };

  const getPixTypeIcon = (type) => {
    const pixType = PIX_TYPES.find(p => p.value === type);
    return pixType?.icon || Key;
  };

  // Mostrar loading enquanto verifica autenticação
  if (!authChecked || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Configurações
          </h1>
          <p className="text-sm text-muted-foreground">
            Gerencie seu perfil, horários e dados de pagamento
          </p>
        </div>

        {/* Alerta se Pix não configurado */}
        {!loadingPayout && !hasPayoutConfigured && (
          <Alert className="mb-6 bg-yellow-50 border-yellow-400 border-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            <AlertDescription className="text-yellow-800 ml-2">
              <strong>⚠️ Cadastre sua chave Pix para solicitar repasses!</strong>
              <p className="text-sm mt-1">
                Você não poderá solicitar repasses até configurar sua chave Pix.
                Seu faturamento continua sendo registrado normalmente.
              </p>
              <Button 
                size="sm" 
                className="mt-2 bg-yellow-600 hover:bg-yellow-700"
                onClick={() => setActiveTab('payment')}
              >
                Configurar agora
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Perfil
            </TabsTrigger>
            <TabsTrigger value="availability" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Horários
            </TabsTrigger>
            <TabsTrigger value="payment" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Pagamento
              {!hasPayoutConfigured && (
                <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Tab: Perfil - Somente Leitura */}
          <TabsContent value="profile">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Dados do Perfil
                </h2>
                <Badge variant="secondary" className="bg-slate-100 text-slate-600" data-testid="profile-locked-badge">
                  <Shield className="w-3 h-3 mr-1" />
                  Dados cadastrais protegidos
                </Badge>
              </div>

              <p className="text-xs text-muted-foreground mb-4" data-testid="profile-locked-subtitle">
                Alguns dados cadastrais não podem ser alterados após aprovação da conta.
              </p>
              
              <p className="text-sm text-muted-foreground mb-6">
                Estes dados foram definidos no momento do cadastro. Para alterações, entre em contato com o suporte.
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-muted-foreground flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Nome do Lava-Jato
                  </Label>
                  <Input
                    value={profile.business_name}
                    disabled
                    className="bg-slate-50 text-slate-700 cursor-not-allowed"
                  />
                </div>
                
                <div>
                  <Label className="text-muted-foreground flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Nome do Responsável
                  </Label>
                  <Input
                    value={profile.owner_name}
                    disabled
                    className="bg-slate-50 text-slate-700 cursor-not-allowed"
                  />
                </div>
                
                <div>
                  <Label className="text-muted-foreground flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    E-mail
                  </Label>
                  <Input
                    type="email"
                    value={profile.email}
                    disabled
                    className="bg-slate-50 text-slate-700 cursor-not-allowed"
                  />
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Tab: Disponibilidade */}
          <TabsContent value="availability">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Horários de Funcionamento
              </h2>
              
              <div className="space-y-4">
                {DAYS_OF_WEEK.map(day => {
                  const dayAvailability = availability.find(a => a.day === day.id);
                  const isActive = !!dayAvailability;
                  
                  return (
                    <div key={day.id} className={`p-4 rounded-lg border ${isActive ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={isActive}
                            onCheckedChange={() => toggleDayAvailability(day.id)}
                          />
                          <span className={`font-medium ${isActive ? 'text-green-800' : 'text-gray-500'}`}>
                            {day.name}
                          </span>
                        </div>
                        
                        {isActive && (
                          <Badge className="bg-green-100 text-green-800">Aberto</Badge>
                        )}
                      </div>
                      
                      {isActive && (
                        <div className="flex items-center gap-4 mt-3">
                          <div className="flex items-center gap-2">
                            <Label className="text-sm">Das</Label>
                            <Input
                              type="time"
                              value={dayAvailability?.start || '08:00'}
                              onChange={(e) => updateDayTime(day.id, 'start', e.target.value)}
                              className="w-28"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-sm">às</Label>
                            <Input
                              type="time"
                              value={dayAvailability?.end || '18:00'}
                              onChange={(e) => updateDayTime(day.id, 'end', e.target.value)}
                              className="w-28"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                
                <div className="flex justify-end pt-2">
                  <Button
                    onClick={handleSaveAvailability}
                    disabled={saving}
                    variant="secondary"
                    size="sm"
                    className="px-4"
                    data-testid="save-hours-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Salvar Horários
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Tab: Dados de Pagamento (Pix) */}
          <TabsContent value="payment">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Banknote className="w-5 h-5" />
                Dados de Pagamento (Pix)
              </h2>
              
              {/* Status do Pix */}
              {hasPayoutConfigured ? (
                <Alert className="mb-6 bg-green-50 border-green-300">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <AlertDescription className="text-green-800 ml-2">
                    <strong>✅ Repasses configurados!</strong>
                    <p className="text-sm mt-1">
                      Você receberá seus repasses via Pix após cada solicitação aprovada pela plataforma.
                    </p>
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="mb-6 bg-yellow-50 border-yellow-300">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  <AlertDescription className="text-yellow-800 ml-2">
                    <strong>Dados bancários para repasse</strong>
                    <p className="text-sm mt-1">
                      Você precisa cadastrar uma chave Pix válida para solicitar repasses dos serviços realizados.
                    </p>
                  </AlertDescription>
                </Alert>
              )}
              
              {/* Aviso se tem serviço em andamento */}
              {hasInProgressService && (
                <Alert className="mb-6 bg-blue-50 border-blue-300">
                  <Shield className="h-5 w-5 text-blue-600" />
                  <AlertDescription className="text-blue-800 ml-2">
                    <strong>Serviço em andamento</strong>
                    <p className="text-sm mt-1">
                      Não é possível alterar os dados de pagamento enquanto houver um serviço em andamento.
                    </p>
                  </AlertDescription>
                </Alert>
              )}
              
              {loadingPayout ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Dados atuais (se existir) */}
                  {existingPayout && (
                    <div className="p-4 bg-slate-50 rounded-lg border mb-4">
                      <p className="text-sm text-muted-foreground mb-2">Dados atuais:</p>
                      <div className="flex items-center gap-3">
                        {React.createElement(getPixTypeIcon(existingPayout.pix_type), { className: "w-5 h-5 text-primary" })}
                        <div>
                          <p className="font-medium">{existingPayout.holder_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {PIX_TYPES.find(p => p.value === existingPayout.pix_type)?.label}: {existingPayout.pix_key_masked}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Formulário */}
                  <div>
                    <Label>Tipo de Chave Pix *</Label>
                    <Select 
                      value={payoutData.pix_type} 
                      onValueChange={(value) => setPayoutData({ ...payoutData, pix_type: value, pix_key: '' })}
                      disabled={hasInProgressService}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo de chave" />
                      </SelectTrigger>
                      <SelectContent>
                        {PIX_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <type.icon className="w-4 h-4" />
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Chave Pix *</Label>
                    <Input
                      value={payoutData.pix_key}
                      onChange={(e) => setPayoutData({ ...payoutData, pix_key: e.target.value })}
                      placeholder={PIX_TYPES.find(p => p.value === payoutData.pix_type)?.placeholder || 'Digite sua chave Pix'}
                      disabled={hasInProgressService || !payoutData.pix_type}
                    />
                  </div>
                  
                  <div>
                    <Label>Nome do Titular *</Label>
                    <Input
                      value={payoutData.holder_name}
                      onChange={(e) => setPayoutData({ ...payoutData, holder_name: e.target.value })}
                      placeholder="Nome completo do titular da conta"
                      disabled={hasInProgressService}
                    />
                  </div>
                  
                  <div>
                    <Label>CPF/CNPJ do Titular *</Label>
                    <Input
                      value={payoutData.holder_document}
                      onChange={(e) => setPayoutData({ ...payoutData, holder_document: e.target.value })}
                      placeholder="000.000.000-00 ou 00.000.000/0001-00"
                      disabled={hasInProgressService}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Deve ser o mesmo documento vinculado à chave Pix
                    </p>
                  </div>
                  
                  <Button 
                    onClick={handleSavePayout} 
                    disabled={savingPayout || hasInProgressService} 
                    className="w-full"
                  >
                    {savingPayout ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    {hasPayoutConfigured ? 'Atualizar Dados de Pagamento' : 'Salvar Dados de Pagamento'}
                  </Button>
                  
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    🔒 Seus dados são criptografados e protegidos conforme a LGPD.
                  </p>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
